/**
 * useGeoData - Hook for geographic data (estados, municípios, CEP)
 *
 * Manages:
 * - Estados list
 * - Municípios list (filtered by estado)
 * - CEP auto-fill
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../services/apiClient';
import useAuthStore from '../stores/authStore';
import type { Hospital } from './useHospitalData';
import { msg, MESSAGES } from '../utils/messages';

// NOTE: apiClient already has baseURL with /api, so we use relative paths

export interface Estado {
  codigo_uf: number;
  uf: string;
  nome: string;
  regiao: string;
}

export interface Municipio {
  codigo_ibge: number;
  nome: string;
  capital: boolean;
  estado_uf: string;
}

interface UseGeoDataProps {
  selectedHospital: Hospital | null;
  editedHospital: Hospital | null;
  isEditing: boolean;
  setEditedHospital: (hospital: Hospital | null) => void;
}

export function useGeoData({
  selectedHospital,
  editedHospital,
  isEditing,
  setEditedHospital,
}: UseGeoDataProps) {
  const token = useAuthStore((state) => state.token);

  const [estados, setEstados] = useState<Estado[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [loadingCep, setLoadingCep] = useState(false);

  // Ref para evitar buscas duplicadas do mesmo CEP
  const lastFetchedCep = useRef<string>('');

  // Ref para acessar editedHospital atual sem causar re-renders
  const editedHospitalRef = useRef(editedHospital);
  editedHospitalRef.current = editedHospital;

  // Ref para setEditedHospital estável
  const setEditedHospitalRef = useRef(setEditedHospital);
  setEditedHospitalRef.current = setEditedHospital;

  // Fetch estados on mount
  useEffect(() => {
    const fetchEstados = async () => {
      try {
        const response = await apiClient.get(`/estados`);
        setEstados(response.data);
      } catch (err) {
        console.error('Erro ao buscar estados:', err);
      }
    };

    fetchEstados();
  }, []);

  // Fetch municípios when estado changes
  useEffect(() => {
    const fetchMunicipios = async () => {
      const estadoAtual = isEditing ? editedHospital?.estado : selectedHospital?.estado;

      if (!estadoAtual) {
        setMunicipios([]);
        return;
      }

      try {
        const response = await apiClient.get(
          `/municipios/estado/${estadoAtual}`
        );
        setMunicipios(response.data);
      } catch (err) {
        console.error('Erro ao buscar municípios:', err);
        setMunicipios([]);
      }
    };

    fetchMunicipios();
  }, [selectedHospital?.estado, editedHospital?.estado, isEditing, token]);

  // #5: ao ENTRAR no modo de edição (ou trocar de hospital em edição),
  // semear lastFetchedCep com o CEP ATUAL para que a busca automática NÃO
  // dispare só por abrir a edição — nada é alterado ao clicar "Editar".
  // A busca só ocorre quando o usuário REALMENTE altera o CEP (vira um valor
  // diferente do semeado). Ao sair da edição, reseta.
  useEffect(() => {
    if (!isEditing) {
      lastFetchedCep.current = '';
      return;
    }
    const cepAtualLimpo = (editedHospital?.cep || selectedHospital?.cep || '')
      .replace(/\D/g, '');
    lastFetchedCep.current = cepAtualLimpo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, selectedHospital?.id]);

  // CEP auto-fill with debounce - usa apenas cep como dependência
  const cepAtual = editedHospital?.cep;

  useEffect(() => {
    if (!cepAtual || !isEditing) {
      setLoadingCep(false);
      return;
    }

    // Remove formatting
    const cepLimpo = cepAtual.replace(/\D/g, '');

    // Only search if exactly 8 digits
    if (cepLimpo.length !== 8) {
      setLoadingCep(false);
      return;
    }

    // Evitar buscar o mesmo CEP novamente
    if (lastFetchedCep.current === cepLimpo) {
      setLoadingCep(false);
      return;
    }

    const buscarCepAutomatico = async () => {
      try {
        setLoadingCep(true);
        const response = await apiClient.get(
          `/cep/${cepLimpo}`
        );

        // Marcar como buscado ANTES de atualizar o estado
        lastFetchedCep.current = cepLimpo;

        if (response.data.error) {
          console.warn('CEP não encontrado:', response.data.message);
          msg.warning(MESSAGES.CEP.NOT_FOUND(cepLimpo));
          setLoadingCep(false);
          return;
        }

        // Usar ref para obter o estado atual do hospital
        const currentHospital = editedHospitalRef.current;
        if (currentHospital) {
          // #5: NAO sobrescrever 'endereco' se o usuario ja tem um preenchido —
          // o campo guarda logradouro + NUMERO, e o ViaCEP so traz o logradouro,
          // entao reprocessar o CEP apagava o numero. Mantemos o endereco atual
          // (preserva o numero) e so auto-preenchemos quando estiver vazio.
          // Bairro/cidade/estado seguem atualizando (nao contem o numero).
          const enderecoAtual = (currentHospital.endereco || '').trim();
          setEditedHospitalRef.current({
            ...currentHospital,
            cep: response.data.cep,
            endereco: enderecoAtual ? currentHospital.endereco : response.data.endereco,
            bairro: response.data.bairro,
            cidade: response.data.cidade,
            estado: response.data.estado
          });
        }

        msg.success(MESSAGES.CEP.FOUND);
        console.log('✅ Endereço encontrado e preenchido automaticamente!');
      } catch (err: any) {
        console.error('Erro ao buscar CEP:', err);
        // Mostrar mensagem amigável ao usuário
        if (err.response?.status === 503 || err.response?.status === 500) {
          msg.warning(MESSAGES.CEP.SERVICE_ERROR);
        } else if (err.response?.status === 404) {
          msg.warning(MESSAGES.CEP.NOT_FOUND(cepLimpo));
        } else {
          msg.warning(MESSAGES.CEP.SERVICE_ERROR);
        }
      } finally {
        setLoadingCep(false);
      }
    };

    // Debounce: wait 500ms after stop typing
    const timer = setTimeout(buscarCepAutomatico, 500);

    return () => clearTimeout(timer);
  }, [cepAtual, isEditing, token]);

  return {
    estados,
    municipios,
    loadingCep,
  };
}
